import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { qk } from '@/lib/queryKeys';
import { DEFAULT_TIMEZONE, setTimezone } from '@/lib/dates';
import { MODULE_KEYS, type ModuleKey } from './modules';
import { FALLBACK_BRAND, type AppSettings, type Brand } from './settings.types';

type Row = { key: string; value: unknown };

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function mapSettings(rows: Row[]): AppSettings {
  const raw: Record<string, unknown> = {};
  for (const row of rows) raw[row.key] = row.value;

  const modulesValue = (raw.modules ?? {}) as Record<string, unknown>;
  const modules = Object.fromEntries(
    MODULE_KEYS.map((key) => [key, modulesValue[key] === true]),
  ) as Record<ModuleKey, boolean>;

  const brand: Brand = {
    name: asString(raw['brand.name']) ?? FALLBACK_BRAND.name,
    app_title: asString(raw['brand.app_title']) ?? FALLBACK_BRAND.app_title,
    primary_color: asString(raw['brand.primary_color']),
    accent_color: asString(raw['brand.accent_color']),
    logo_white: asString(raw['brand.logo_white']),
    logo_ink: asString(raw['brand.logo_ink']) ?? asString(raw['brand.logo_path']),
    icon_192: asString(raw['brand.icon_192']),
    icon_512: asString(raw['brand.icon_512']),
  };

  return {
    brand,
    modules,
    timezone: asString(raw.timezone) ?? DEFAULT_TIMEZONE,
    appUrl: asString(raw['app.url']) ?? window.location.origin,
    raw,
  };
}

export const FALLBACK_SETTINGS: AppSettings = mapSettings([]);

async function fetchSettings(): Promise<AppSettings> {
  const { data, error } = await supabase.from('app_settings').select('key, value');
  if (error) throw error;
  return mapSettings((data ?? []) as Row[]);
}

/**
 * כל ההגדרות בקריאה אחת בעליית האפליקציה (staleTime 10 דקות),
 * והמיתוג מוחל בזמן ריצה על :root (מסמך התכנון, סעיף 4.1).
 */
export function useSettingsQuery() {
  const query = useQuery({
    queryKey: qk.settings,
    queryFn: fetchSettings,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const settings = query.data ?? FALLBACK_SETTINGS;

  useEffect(() => {
    setTimezone(settings.timezone);
    document.title = settings.brand.app_title;

    const root = document.documentElement;
    if (settings.brand.primary_color) {
      root.style.setProperty('--charcoal', settings.brand.primary_color);
    }
    if (settings.brand.accent_color) {
      root.style.setProperty('--olive', settings.brand.accent_color);
    }
  }, [settings]);

  return { ...query, settings };
}
